/** Admin realtime (Socket.IO `/admin`): session rooms, incoming feedback, presence, notifications — batched at 250 ms. */
import { ADMIN_NAMESPACE, REALTIME_PATH, type AdminToServerEvents, type ServerToAdminEvents } from '@uatp/shared';
import { useEffect, useRef } from 'react';
import { io, type Socket } from 'socket.io-client';

let socket: Socket<ServerToAdminEvents, AdminToServerEvents> | null = null;

/**
 * Session rooms are shared by every mounted subscriber (overview, live monitor, inbox…): the room is joined when
 * the first one mounts, left when the last one unmounts, and re-joined after every (re)connect.
 */
const watchers = new Map<string, number>();

export function adminSocket(): Socket<ServerToAdminEvents, AdminToServerEvents> {
  if (!socket) {
    const s: Socket<ServerToAdminEvents, AdminToServerEvents> = io(ADMIN_NAMESPACE, {
      path: REALTIME_PATH,
      withCredentials: true,
      transports: ['websocket', 'polling'],
    });
    s.on('connect', () => {
      for (const id of watchers.keys()) s.emit('session.watch', id);
    });
    socket = s;
  }
  return socket;
}

export function disconnectRealtime(): void {
  socket?.disconnect();
  socket = null;
}

export function watchSession(sessionId: string): void {
  const s = adminSocket();
  const n = watchers.get(sessionId) ?? 0;
  watchers.set(sessionId, n + 1);
  // While disconnected the connect handler joins it (avoids a buffered duplicate).
  if (n === 0 && s.connected) s.emit('session.watch', sessionId);
}

export function unwatchSession(sessionId: string): void {
  const n = watchers.get(sessionId) ?? 0;
  if (n > 1) {
    watchers.set(sessionId, n - 1);
    return;
  }
  watchers.delete(sessionId);
  if (socket?.connected) socket.emit('session.unwatch', sessionId);
}

/** Test hook: the number of subscribers holding a session room. */
export function watcherCount(sessionId: string): number {
  return watchers.get(sessionId) ?? 0;
}

type EventName = keyof ServerToAdminEvents;
type Payload<E extends EventName> = Parameters<ServerToAdminEvents[E]>[0];

/** Subscribes to events (optionally joining a session room) and delivers them in 250 ms batches (Technical Specification §8). */
export function useRealtime<E extends EventName>(events: E[], onBatch: (batch: { event: E; payload: Payload<E> }[]) => void, sessionId?: string | null): void {
  const handler = useRef(onBatch);
  handler.current = onBatch;
  const key = events.join(',');
  useEffect(() => {
    const s = adminSocket();
    let buffer: { event: E; payload: Payload<E> }[] = [];
    let timer: ReturnType<typeof setTimeout> | null = null;
    const flush = () => {
      timer = null;
      const b = buffer;
      buffer = [];
      if (b.length) handler.current(b);
    };
    const listeners = events.map((event) => {
      const fn = (payload: Payload<E>) => {
        buffer.push({ event, payload });
        timer ??= setTimeout(flush, 250);
      };
      s.on(event as never, fn as never);
      return [event, fn] as const;
    });
    if (sessionId) watchSession(sessionId);
    return () => {
      for (const [event, fn] of listeners) s.off(event as never, fn as never);
      if (sessionId) unwatchSession(sessionId);
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, sessionId]);
}
